
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ReadyToStartSection from '../../components/feature/ReadyToStartSection';
import Button from '../../components/base/Button';

export default function Contact() {
  const [formData, setFormData] = useState({});
  const [formUrl, setFormUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const getFormUrl = async () => {
      try {
        const response = await fetch('/api/get-form-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: 'Tour and Contact Request' })
        });
        const data = await response.json();
        setFormUrl(data.submitAddr);
      } catch (error) {
        console.error('Error getting form URL:', error);
      }
    };

    getFormUrl();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formUrl) return;

    setIsSubmitting(true);
    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, value);
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataEncoded
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({});
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg[#FFF7E8]">
      <Header />
      
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: '"Fredoka", sans-serif' }}>
          Contact Us
        </h1>
      </div>

      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-32 px-4 sm:px-6 lg:px-8 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(46, 89, 57, 0.75), rgba(46, 89, 57, 0.75)), url('https://public.readdy.ai/ai/img_res/4f5b154e474d9df2448e5375ab1d600f.jpg')`,
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Contact & Tours
          </h1>
          <p className="text-xl md:text-2xl text-white/95 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            We'd love to show you around! Schedule a visit or reach out with any questions — our team is here to help you learn more about Creative Journeys.
          </p>
        </div>
      </section>

      {/* Connect With Us Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 -mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Details Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
              <div className="w-16 h-16 bg-[#2E5939] rounded-full flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#333333] mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Connect With Us
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#BFE6CC] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="ri-map-pin-line text-2xl text-[#2E5939]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] mb-1 text-lg">Address</h4>
                    <p className="text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      423 Edgewood Rd, Edgewood, MD 21040
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#A8D8F0] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="ri-phone-line text-2xl text-[#2E5939]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] mb-1 text-lg">Phone</h4>
                    <a 
                      href="tel:410-676-9002" 
                      className="text-[#2E5939] hover:text-[#A8D8F0] transition-colors duration-200" 
                      style={{ fontFamily: 'Open Sans, sans-serif' }}
                    >
                      (410) 676-9002
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#FFD84C] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="ri-mail-line text-2xl text-[#2E5939]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] mb-1 text-lg">Email</h4>
                    <a 
                      href="mailto:info@CreativeJourneysChildCare.com" 
                      className="text-[#2E5939] hover:text-[#A8D8F0] transition-colors duration-200 break-all" 
                      style={{ fontFamily: 'Open Sans, sans-serif' }}
                    >
                      info@CreativeJourneysChildCare.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#BFE6CC] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="ri-time-line text-2xl text-[#2E5939]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] mb-2 text-lg">Hours</h4>
                    <div className="text-[#333333]/80 space-y-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      <p>Monday – Friday: 6:30 AM – 5:00 PM</p>
                      <p>Saturday – Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social & Quick Links Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
              <div className="w-16 h-16 bg-[#A8D8F0] rounded-full flex items-center justify-center mb-6">
                <i className="ri-share-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#333333] mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Follow Us
              </h3>
              
              <p className="text-[#333333]/80 mb-8 text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Stay connected with Creative Journeys! Follow us on social media to see daily activities, special events, and updates from our learning community.
              </p>

              <div className="space-y-4 mb-10">
                <a 
                  href="https://www.facebook.com/CreativeJourneysChildCare/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 bg-[#FFF7E8] rounded-xl hover:bg-[#BFE6CC]/30 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-[#2E5939] group-hover:bg-[#A8D8F0] rounded-full flex items-center justify-center flex-shrink-0 mr-4 transition-colors duration-200">
                    <i className="ri-facebook-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] text-lg">Facebook</h4>
                    <p className="text-[#333333]/60 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Follow us for daily updates
                    </p>
                  </div>
                </a>

                <a 
                  href="https://share.google/dDQoXhBBKZEDyPW7w" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 bg-[#FFF7E8] rounded-xl hover:bg-[#BFE6CC]/30 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-[#2E5939] group-hover:bg-[#A8D8F0] rounded-full flex items-center justify-center flex-shrink-0 mr-4 transition-colors duration-200">
                    <i className="ri-google-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] text-lg">Google</h4>
                    <p className="text-[#333333]/60 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Read reviews and ratings
                    </p>
                  </div>
                </a>

                <a 
                  href="https://www.youtube.com/@creativejourneyschildcarel5091" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 bg-[#FFF7E8] rounded-xl hover:bg-[#BFE6CC]/30 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-14 h-14 bg-[#2E5939] group-hover:bg-[#A8D8F0] rounded-full flex items-center justify-center flex-shrink-0 mr-4 transition-colors duration-200">
                    <i className="ri-youtube-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#333333] text-lg">YouTube</h4>
                    <p className="text-[#333333]/60 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Watch our videos
                    </p>
                  </div>
                </a>
              </div>

              <div className="bg-[#A8D8F0]/10 rounded-xl p-6 border-l-4 border-[#A8D8F0]">
                <p className="text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  <i className="ri-information-line text-[#2E5939] mr-2"></i>
                  Have questions? Feel free to reach out via phone or email, or fill out the contact form below. We typically respond within 24 hours!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Footer */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg[#FFF7E8]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            We can't wait to welcome your family to Creative Journeys!
          </h2>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
